/**
 * useConversations Hook
 * 
 * Manages conversation list state with real-time updates.
 * 
 * Features:
 * - IndexedDB caching for instant load
 * - Real-time Firestore subscription for live updates
 * - Background sync - cached data shown first, then updated silently
 * - Pinned conversations at top (sorted by pin time, most recent first)
 * - Mark as read/unread
 * - Archive conversations (hide from list, auto-unarchive on new message)
 * - Avatar preloading for smooth transitions
 */

import { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import { toast } from 'sonner';
import {
  subscribeToConversations,
  getMoreConversations,
  getConversationDisplayName,
  pinConversation,
  unpinConversation,
  markConversationAsReadManual,
  markConversationAsUnread,
  CONVERSATION_TYPES,
  PAGE_SIZE,
  archiveConversation,
  archiveConversationForEveryone,
  subscribeToArchivedConversations
} from '@/lib/firestore';
import { 
  getCachedConversationList, 
  cacheConversationList 
} from '@/lib/messageCache';
import { useAvatarCache } from '@/contexts';
import { onFirebaseAuthChange, isFirebaseSignedIn } from '@/lib/firebase';

/**
 * Preload an image into browser cache
 */
function preloadImage(url) {
  if (!url) return;
  const img = new Image();
  img.src = url;
}

/**
 * Sort conversations: pinned first, then by most recent activity
 */
function sortConversations(conversations, userId) {
  return [...conversations].sort((a, b) => {
    const aIsPinned = a.pinnedBy?.includes(userId);
    const bIsPinned = b.pinnedBy?.includes(userId);

    // Pinned conversations come first
    if (aIsPinned && !bIsPinned) return -1;
    if (!aIsPinned && bIsPinned) return 1;

    // Both pinned: sort by pinnedAt ascending (first pinned stays at top)
    if (aIsPinned && bIsPinned) {
      const aPinnedAt = a.pinnedAt?.[userId] ? new Date(a.pinnedAt[userId]) : new Date(0);
      const bPinnedAt = b.pinnedAt?.[userId] ? new Date(b.pinnedAt[userId]) : new Date(0);
      return aPinnedAt - bPinnedAt;
    }

    // Neither pinned: sort by most recent activity
    // Null updatedAt (from pending serverTimestamp()) is treated as "now" so
    // the conversation the user just messaged stays at the top, not the bottom.
    const aTime = a.updatedAt?.toDate?.() || new Date();
    const bTime = b.updatedAt?.toDate?.() || new Date();
    return bTime - aTime;
  });
}

export function useConversations(userId) {
  const [allConversations, setAllConversations] = useState([]);
  const [archivedIds, setArchivedIds] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isFromCache, setIsFromCache] = useState(false);
  const [hasMoreConversations, setHasMoreConversations] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [fbAuthenticated, setFbAuthenticated] = useState(isFirebaseSignedIn());
  
  const hasSyncedRef = useRef(false);
  const lastDocRef = useRef(null);
  const olderConversationsRef = useRef([]);
  
  // Get avatar cache functions for cache-first lookups
  const { getCachedAvatar } = useAvatarCache();

  // Track Firebase Auth state — re-subscribe when auth completes
  useEffect(() => {
    return onFirebaseAuthChange((user) => {
      setFbAuthenticated(!!user);
    });
  }, []);

  // Filter out archived conversations - memoized to prevent unnecessary re-renders
  const conversations = useMemo(() => {
    return allConversations.filter(c => !archivedIds.includes(c.id));
  }, [allConversations, archivedIds]);

  // Preload avatars for conversations (browser image cache only)
  const preloadAvatars = useCallback((convs) => {
    convs.forEach(conv => {
      if (conv.type !== CONVERSATION_TYPES.GROUP) {
        const otherUserId = conv.participants?.find(id => id !== userId);
        if (otherUserId) {
          // Prefer cached (MongoDB-sourced) avatar; fall back to Firestore for image preloading
          const cachedUrl = getCachedAvatar(otherUserId);
          const firestoreUrl = conv.participantDetails?.[otherUserId]?.avatar;
          const urlToPreload = cachedUrl || firestoreUrl;
          if (urlToPreload) {
            preloadImage(urlToPreload);
          }
          // Do NOT call cacheAvatar with Firestore data — the cache
          // is seeded from MongoDB via team members API at app load
        }
      }
    });
  }, [userId, getCachedAvatar]);

  // Load cached conversations first, then sync with Firestore
  useEffect(() => {
    if (!userId || !fbAuthenticated) {
      return;
    }

    let isSubscribed = true;
    hasSyncedRef.current = false;
    lastDocRef.current = null;
    olderConversationsRef.current = [];
    setHasMoreConversations(false);

    const loadConversations = async () => {
      // Step 1: Load from cache immediately (instant)
      try {
        const cached = await getCachedConversationList(userId);
        
        if (isSubscribed && cached.length > 0) {
          // Restore original id from originalId
          const restoredCached = cached.map(c => ({
            ...c,
            id: c.originalId || c.id.replace(`${userId}_`, '')
          }));
          
          const sorted = sortConversations(restoredCached, userId);
          setAllConversations(sorted);
          setIsFromCache(true);
          setLoading(false); // Stop loading - we have data!
          
          // Preload avatars from cache
          preloadAvatars(sorted);
        }
      } catch (err) {
        console.error('[useConversations] Cache read error:', err);
      }

      // Step 2: Subscribe to Firestore for real-time updates (limited)
      const unsubscribe = subscribeToConversations(
        userId, 
        (fetchedConversations, lastDoc) => {
          if (!isSubscribed) return;
          
          // Preload avatars
          preloadAvatars(fetchedConversations);
          
          // Track the last document for pagination
          lastDocRef.current = lastDoc || null;
          setHasMoreConversations(fetchedConversations.length >= PAGE_SIZE.CONVERSATIONS);
          
          // Merge real-time conversations with any older loaded conversations
          const realtimeIds = new Set(fetchedConversations.map(c => c.id));
          // Keep older conversations that haven't been updated into the real-time window
          const stillOlder = olderConversationsRef.current.filter(c => !realtimeIds.has(c.id));
          olderConversationsRef.current = stillOlder;
          
          const merged = [...fetchedConversations, ...stillOlder];
          const sorted = sortConversations(merged, userId);
          setAllConversations(sorted);
          setLoading(false);
          setIsFromCache(false);
          
          // Cache for next time (don't await)
          if (!hasSyncedRef.current) {
            hasSyncedRef.current = true;
            cacheConversationList(userId, merged).catch(err => {
              console.error('[useConversations] Cache write error:', err);
            });
          }
        },
        (error) => {
          if (!isSubscribed) return;
          console.error('Conversation subscription error:', error);
          setError(error.message || 'Failed to load conversations');
          setLoading(false);
        }
      );

      return unsubscribe;
    };

    let unsubscribe;
    loadConversations().then(unsub => {
      unsubscribe = unsub;
    });

    return () => {
      isSubscribed = false;
      if (unsubscribe) unsubscribe();
    };
  }, [userId, fbAuthenticated, preloadAvatars]);

  // Subscribe to archived conversations
  useEffect(() => {
    if (!userId) return;

    const unsubscribe = subscribeToArchivedConversations(userId, (archived) => {
      setArchivedIds(archived);
    });

    return () => unsubscribe();
  }, [userId]);

  // Pin a conversation
  const pin = useCallback(async (conversationId) => {
    try {
      await pinConversation(conversationId, userId);
    } catch (err) {
      console.error('Error pinning conversation:', err);
      toast.error("Couldn't pin this conversation. Please try again.");
    }
  }, [userId]);

  // Unpin a conversation
  const unpin = useCallback(async (conversationId) => {
    try {
      await unpinConversation(conversationId, userId);
    } catch (err) {
      console.error('Error unpinning conversation:', err);
      toast.error("Couldn't unpin this conversation. Please try again.");
    }
  }, [userId]);

  // Toggle pin (legacy, kept for backward compatibility)
  const togglePin = useCallback(async (conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    if (!conversation) return;

    const pinned = conversation.pinnedBy?.includes(userId);
    
    if (pinned) {
      await unpin(conversationId);
    } else {
      await pin(conversationId);
    }
  }, [conversations, userId, pin, unpin]);

  // Check if a conversation is pinned
  const isPinned = useCallback((conversationId) => {
    const conversation = conversations.find(c => c.id === conversationId);
    return conversation?.pinnedBy?.includes(userId) || false;
  }, [conversations, userId]);

  // Mark conversation as read manually
  const markAsRead = useCallback(async (conversationId) => {
    try {
      await markConversationAsReadManual(conversationId, userId);
    } catch (err) {
      console.error('Error marking as read:', err);
      toast.error("Couldn't mark as read. Please try again.");
    }
  }, [userId]);

  // Mark conversation as unread manually
  const markAsUnread = useCallback(async (conversationId) => {
    try {
      await markConversationAsUnread(conversationId, userId);
    } catch (err) {
      console.error('Error marking as unread:', err);
      toast.error("Couldn't mark as unread. Please try again.");
    }
  }, [userId]);

  // Archive a conversation (hide from list)
  const archive = useCallback(async (conversationId) => {
    try {
      await archiveConversation(userId, conversationId);
    } catch (err) {
      console.error('Error archiving conversation:', err);
      toast.error("Couldn't archive this conversation. Please try again.");
    }
  }, [userId]);

  // Archive a conversation for every participant (chat creator / church admins).
  // The hide itself still comes from users/{uid}.archivedConversations, so this
  // list updates through the same archived-ids subscription as `archive`.
  const archiveForEveryone = useCallback(async (conversationId) => {
    try {
      await archiveConversationForEveryone(userId, conversationId);
      toast.success('Archived for everyone');
      return true;
    } catch (err) {
      console.error('Error archiving conversation for everyone:', err);
      toast.error(err?.message || "Couldn't archive for everyone");
      return false;
    }
  }, [userId]);

  // Check if conversation is manually marked as unread
  const isManuallyUnread = useCallback((conversationId) => {
    const conversation = allConversations.find(c => c.id === conversationId);
    return conversation?.manuallyMarkedUnread?.[userId] || false;
  }, [allConversations, userId]);

  // Get display name for a conversation
  const getDisplayName = useCallback((conversation) => {
    return getConversationDisplayName(conversation, userId);
  }, [userId]);

  // Get avatar for a conversation (cache-first, Firestore fallback)
  const getAvatar = useCallback((conversation) => {
    if (conversation.type === CONVERSATION_TYPES.GROUP) return null;
    const otherUserId = conversation.participants?.find(id => id !== userId);
    if (!otherUserId) return null;
    return getCachedAvatar(otherUserId)
      || conversation.participantDetails?.[otherUserId]?.avatar
      || null;
  }, [userId, getCachedAvatar]);

  // Get participant ID for 1-on-1 conversations (for avatar caching)
  const getParticipantId = useCallback((conversation) => {
    if (conversation.type === 'group') return null;
    // For DM, find the other participant
    const otherParticipant = conversation.participants?.find(p => p !== userId);
    return otherParticipant || null;
  }, [userId]);

  // Load more conversations (pagination)
  const loadMore = useCallback(async () => {
    if (!lastDocRef.current || loadingMore || !hasMoreConversations) return;
    setLoadingMore(true);
    try {
      const result = await getMoreConversations(userId, lastDocRef.current);
      if (result.conversations.length > 0) {
        preloadAvatars(result.conversations);
        olderConversationsRef.current = [...olderConversationsRef.current, ...result.conversations];
        setAllConversations(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newConvs = result.conversations.filter(c => !existingIds.has(c.id));
          return sortConversations([...prev, ...newConvs], userId);
        });
      }
      lastDocRef.current = result.lastDoc || lastDocRef.current;
      setHasMoreConversations(result.hasMore);
    } catch (err) {
      console.error('Error loading more conversations:', err);
    } finally {
      setLoadingMore(false);
    }
  }, [userId, loadingMore, hasMoreConversations, preloadAvatars]);

  return {
    conversations,
    loading,
    error,
    isFromCache,
    hasMoreConversations,
    loadingMore,
    loadMore,
    pin,
    unpin,
    togglePin,
    isPinned,
    markAsRead,
    markAsUnread,
    archive,
    archiveForEveryone,
    isManuallyUnread,
    getDisplayName,
    getAvatar,
    getParticipantId
  };
}
